/**
 * PESQUISA DE CLIMA ORGANIZACIONAL — o questionário OFICIAL da SERCAMP.
 *
 * É a transcrição fiel do formulário em papel ("Pesquisa de Clima
 * Organizacional — SERCAMP SERVIÇOS E COMERCIAL LTDA."): a apresentação, o
 * setor, o tempo de empresa, a escala de 1 a 5, as 9 afirmações e a pergunta
 * aberta do fim.
 *
 * Fica em src/ (e não em server/) porque as DUAS pontas usam a mesma lista: a
 * tela do RH e a página pública montam o formulário a partir daqui, e o
 * servidor valida a resposta contra ela — mesmo caminho de
 * src/avaliacao-modelos.ts, que o server/avaliacao-links.ts importa.
 */

export const CLIMA_TITULO = "Pesquisa de Clima Organizacional";
export const CLIMA_EMPRESA = "SERCAMP SERVIÇOS E COMERCIAL LTDA.";

export const CLIMA_APRESENTACAO =
  "Esta pesquisa tem como objetivo conhecer a percepção dos colaboradores sobre o ambiente de trabalho da Sercamp.";

export const CLIMA_CONFIDENCIALIDADE =
  "As respostas serão utilizadas para identificar pontos positivos e oportunidades de melhoria.";

export const CLIMA_AGRADECIMENTO =
  "Agradecemos sua participação e sinceridade. Sua opinião é importante para construirmos uma Sercamp cada vez mais humana, transparente, segura e comprometida com a excelência.";

/** "Tempo de empresa" do cabeçalho do formulário (as três marcações do papel). */
export const CLIMA_TEMPOS_EMPRESA = [
  "Menos de 1 ano",
  "De 1 a 3 anos",
  "Mais de 3 anos",
] as const;

export type TempoEmpresa = (typeof CLIMA_TEMPOS_EMPRESA)[number];

export interface NivelClima {
  valor: number;
  rotulo: string;
}

/** Escala de avaliação do formulário (1 a 5). */
export const CLIMA_ESCALA: NivelClima[] = [
  { valor: 1, rotulo: "Discordo totalmente" },
  { valor: 2, rotulo: "Discordo parcialmente" },
  { valor: 3, rotulo: "Nem concordo nem discordo" },
  { valor: 4, rotulo: "Concordo parcialmente" },
  { valor: 5, rotulo: "Concordo totalmente" },
];

export interface PerguntaClima {
  /** Identificador estável da pergunta ("q1"… "q10"). */
  id: string;
  texto: string;
  /** "escala" = 1 a 5 (as 9 afirmações); "aberta" = texto livre (a 10ª). */
  tipo: "escala" | "aberta";
  /** Agrupamento usado no consolidado (liderança, reconhecimento…). */
  dimensao: string;
  /**
   * Pergunta de RECOMENDAÇÃO ("indicaria a empresa"). É a que vira o eNPS:
   * 5 = promotor, 4 = neutro, 1-3 = detrator.
   */
  enps?: boolean;
}

/** As 10 perguntas do formulário, na ordem do papel. */
export const CLIMA_PERGUNTAS: PerguntaClima[] = [
  {
    id: "q1",
    texto: "Sinto-me respeitado e bem tratado no ambiente de trabalho.",
    tipo: "escala",
    dimensao: "Respeito e convivência",
  },
  {
    id: "q2",
    texto: "Existe cooperação e bom relacionamento entre os colegas da minha equipe.",
    tipo: "escala",
    dimensao: "Trabalho em equipe",
  },
  {
    id: "q3",
    texto: "Minha liderança comunica com clareza o que espera do meu trabalho.",
    tipo: "escala",
    dimensao: "Liderança",
  },
  {
    id: "q4",
    texto: "Minha liderança me trata com respeito, equilíbrio e justiça.",
    tipo: "escala",
    dimensao: "Liderança",
  },
  {
    id: "q5",
    texto:
      "Recebo as informações, orientações e recursos necessários para realizar minhas atividades.",
    tipo: "escala",
    dimensao: "Condições de trabalho",
  },
  {
    id: "q6",
    texto: "Sinto segurança para dar opiniões, apresentar sugestões ou comunicar problemas.",
    tipo: "escala",
    dimensao: "Comunicação e abertura",
  },
  {
    id: "q7",
    texto: "Meu trabalho é reconhecido e valorizado pela empresa.",
    tipo: "escala",
    dimensao: "Reconhecimento",
  },
  {
    id: "q8",
    texto: "A Sercamp oferece condições adequadas de segurança, organização e bem-estar.",
    tipo: "escala",
    dimensao: "Segurança e bem-estar",
  },
  {
    id: "q9",
    texto:
      "Tenho orgulho de trabalhar na Sercamp e indicaria a empresa como um bom lugar para trabalhar.",
    tipo: "escala",
    dimensao: "Orgulho e recomendação",
    enps: true,
  },
  {
    id: "q10",
    texto: "O que a Sercamp poderia fazer para melhorar o ambiente de trabalho?",
    tipo: "aberta",
    dimensao: "Sugestões",
  },
];

/* ------------------------------------------------------------------ *
 *  Contas do formulário (as mesmas nas duas pontas)                   *
 * ------------------------------------------------------------------ */

/** Rótulo de um valor da escala ("4" → "Concordo parcialmente"). */
export function rotuloEscala(valor: number | null | undefined): string {
  const n = CLIMA_ESCALA.find((e) => e.valor === Number(valor));
  return n ? n.rotulo : "Não respondeu";
}

/** Média (1 a 5) de um conjunto de notas; null quando ninguém respondeu. */
export function mediaClima(notas: number[]): number | null {
  const validas = notas.filter((n) => Number.isFinite(n) && n >= 1 && n <= 5);
  if (validas.length === 0) return null;
  return Math.round((validas.reduce((a, b) => a + b, 0) / validas.length) * 100) / 100;
}

/**
 * Converte a média da escala (1 a 5) em PONTUAÇÃO de 0 a 100 — que é como o
 * indicador de clima já é acompanhado no dashboard e na tabela `engajamento`
 * (campo `pontuacao`). 1 → 0 e 5 → 100.
 */
export function pontuacaoClima(media: number | null): number | null {
  if (media == null) return null;
  return Math.round(((media - 1) / 4) * 1000) / 10;
}

/** Leitura da média para a tela (faixa + tom da etiqueta). */
export function classificacaoClima(media: number | null): { rotulo: string; tom: string } {
  if (media == null) return { rotulo: "Sem respostas", tom: "neutral" };
  if (media >= 4.5) return { rotulo: "Excelente", tom: "ok" };
  if (media >= 4) return { rotulo: "Bom", tom: "ok" };
  if (media >= 3.5) return { rotulo: "Regular", tom: "warn" };
  if (media >= 2.5) return { rotulo: "Atenção", tom: "warn" };
  return { rotulo: "Crítico", tom: "danger" };
}

/** Categoria de eNPS da pergunta de recomendação. */
export function categoriaEnps(nota: number | null | undefined): "Promotor" | "Neutro" | "Detrator" | null {
  const n = Number(nota);
  if (!Number.isFinite(n) || n < 1 || n > 5) return null;
  if (n >= 5) return "Promotor";
  if (n >= 4) return "Neutro";
  return "Detrator";
}

/**
 * Valor de eNPS de UMA resposta: +100 promotor, 0 neutro, -100 detrator.
 * A média desses valores é exatamente o eNPS clássico
 * (% de promotores − % de detratores), então o indicador continua correto ao
 * ser gravado linha a linha na coluna `enps` da tabela `engajamento`.
 */
export function valorEnps(nota: number | null | undefined): number | null {
  const cat = categoriaEnps(nota);
  if (!cat) return null;
  return cat === "Promotor" ? 100 : cat === "Neutro" ? 0 : -100;
}

/** Aceita só um dos três tempos do formulário (o resto vira null). */
export function tempoEmpresaValido(valor: unknown): TempoEmpresa | null {
  const t = String(valor ?? "").trim();
  return (CLIMA_TEMPOS_EMPRESA as readonly string[]).includes(t) ? (t as TempoEmpresa) : null;
}

/** Sugere o tempo de empresa pela data de admissão do cadastro. */
export function tempoEmpresaPorAdmissao(admissao: string | Date | null | undefined): TempoEmpresa | null {
  if (!admissao) return null;
  const d = admissao instanceof Date ? admissao : new Date(admissao);
  if (Number.isNaN(d.getTime())) return null;
  const anos = (Date.now() - d.getTime()) / (365.25 * 86400000);
  if (anos < 0) return null;
  if (anos < 1) return "Menos de 1 ano";
  if (anos <= 3) return "De 1 a 3 anos";
  return "Mais de 3 anos";
}

/**
 * Normaliza a lista de perguntas vinda da tela (ou do banco). O RH pode
 * acrescentar/ajustar perguntas; o que não tiver texto é descartado e, se
 * sobrar nada, volta o questionário oficial.
 */
export function prepararPerguntasClima(lista: unknown): PerguntaClima[] {
  if (!Array.isArray(lista)) return [...CLIMA_PERGUNTAS];
  const perguntas: PerguntaClima[] = [];
  const usados = new Set<string>();
  for (const [i, bruta] of lista.entries()) {
    const item = (typeof bruta === "string" ? { texto: bruta } : bruta) as Record<string, unknown>;
    const texto = String(item?.texto ?? "").trim();
    if (!texto) continue;
    const tipo = String(item?.tipo ?? "escala") === "aberta" ? "aberta" : "escala";
    // ID estável e sem repetição: repetido sobrescreveria a resposta do outro.
    let id = String(item?.id ?? "").trim() || `q${i + 1}`;
    while (usados.has(id)) id = `${id}_`;
    usados.add(id);
    perguntas.push({
      id,
      texto: texto.slice(0, 400),
      tipo,
      dimensao: String(item?.dimensao ?? "Geral").trim().slice(0, 60) || "Geral",
      ...(item?.enps ? { enps: true } : {}),
    });
    if (perguntas.length >= 40) break; // pesquisa de clima não é prova
  }
  return perguntas.length > 0 ? perguntas : [...CLIMA_PERGUNTAS];
}
